import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireAuth } from "@/auth/middleware";
import { getCurrentUserId, getDb, schema } from "@/db/client";
import { sanitizeForSeroval } from "@/server/seroval-safe";
import { DEFAULT_POLICY, type PlanMargins } from "@/calc";

/**
 * Хранение того, как агентство считает.
 *
 * Три справочника, которые Braining правят сами, без релиза:
 *
 *   • план рентабельности — по подотделам и типам проектов;
 *   • наценки на внешних исполнителей — разные по единицам;
 *   • ставки налогов на выплату — подсказки для колонки «Налог».
 *
 * Одна строка на инстанс (0011, 0012): это политика агентства, а не настройка
 * пользователя. Пустые поля — нормальное состояние: чего не заполнили, берётся
 * из `DEFAULT_POLICY`, и на экране написано, что цифра не их.
 *
 * В отличие от ставок рейткарты политика в смету не замораживается. Ставка —
 * цена, и правка прайса задним числом двигала бы суммы прошлых смет. План
 * только сравнивается с фактом; наценка подставляется в момент, когда строку
 * заводят, и дальше живёт в самой строке; ставка налога — тоже поле строки.
 */

/** Наценки на внешних: доля 0…1, где 0,54 значит «цена = расход × 1,54». */
export interface MarkupSettings {
  default: number;
  byUnit: Record<string, number>;
}

/** Ставка налога на выплату: подпись и доля. */
export interface TaxRate {
  label: string;
  rate: number;
}

export interface CalcSettings {
  /** дата последней правки: та же роль, что у версии рейткарты */
  version: string;
  planMargin: PlanMargins;
  markup: MarkupSettings;
  taxRates: TaxRate[];
}

/**
 * Ставки налогов по умолчанию — то, что Аня перечислила на демо: «если это
 * нал, то ноль, если самозанятые, то шесть», «это ИП — здесь мы налог не
 * указываем», «а вот здесь мы платим НДС, и его указываем».
 *
 * В подписях нет цифр намеренно: ставка правится, и «НДС 22%» с полем, где
 * стоит 20, врал бы прямо на экране. Два НДС различаются схемой юрлица —
 * общая у ООО, льготная у ИП на УСН.
 */
export const DEFAULT_TAX_RATES: TaxRate[] = [
  { label: "нал или ИП", rate: 0 },
  { label: "самозанятый", rate: 0.06 },
  { label: "НДС на УСН", rate: 0.05 },
  { label: "НДС общий", rate: 0.22 },
];

export const EMPTY_SETTINGS: CalcSettings = {
  version: "",
  planMargin: { bySubdivision: {}, byType: {} },
  markup: DEFAULT_POLICY.contractorMarkup,
  taxRates: DEFAULT_TAX_RATES,
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

// Доли, а не проценты. Верхняя граница плана 0,99: «100% рентабельности»
// означало бы работу вообще без расходов. Наценка сверху не ограничена —
// перепродажа втрое встречается в архиве и это их право.
const Margins = z.record(z.string(), z.number().min(0).max(0.99));

const SettingsPayload = z.object({
  planMargin: z.object({ bySubdivision: Margins, byType: Margins }),
  markup: z.object({
    default: z.number().min(0).max(20),
    byUnit: z.record(z.string(), z.number().min(0).max(20)),
  }),
  taxRates: z.array(z.object({ label: z.string().min(1).max(40), rate: z.number().min(0).max(1) })),
});

const _loadCalcSettings = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .handler(async (): Promise<CalcSettings> => {
    const [row] = await getDb().select({ data: schema.calcPolicy.data }).from(schema.calcPolicy);
    // Строки нет — политику ещё не трогали. Не сеем: цифр от агентства нет, а
    // выдуманные выглядели бы как их решение.
    if (!row) return sanitizeForSeroval("loadCalcSettings", EMPTY_SETTINGS);

    // Документ мог быть записан, когда полей было меньше: без слияния экран
    // получил бы `undefined` вместо пустого справочника.
    const saved = row.data as Partial<CalcSettings>;
    return sanitizeForSeroval("loadCalcSettings", { ...EMPTY_SETTINGS, ...saved });
  });

const _saveCalcSettings = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: unknown) => SettingsPayload.parse(d))
  .handler(async ({ data }): Promise<CalcSettings> => {
    const db = getDb();
    const next: CalcSettings = { version: today(), ...data };

    const [saved] = await db
      .insert(schema.calcPolicy)
      .values({ data: next, updatedBy: getCurrentUserId() })
      .onConflictDoUpdate({
        target: schema.calcPolicy.singleton,
        set: { data: next, updatedBy: getCurrentUserId(), updatedAt: new Date().toISOString() },
      })
      .returning({ data: schema.calcPolicy.data });

    // RLS вернёт ноль строк, если правки не разрешены: молча «сохранить» в
    // никуда хуже, чем сказать правду.
    if (!saved) throw new Response("Forbidden", { status: 403 });
    return sanitizeForSeroval("saveCalcSettings", saved.data as CalcSettings);
  });

export const loadCalcSettings = _loadCalcSettings;
export const saveCalcSettings = _saveCalcSettings;
